import React, { useEffect, useState } from "react";

// Material
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import { Typography } from "@mui/material";

// Hooks
import useCard from "../../hooks/orders/useCard";
import useNorify from "../../hooks/notify";
import useSearchCards from "../../hooks/cards/useSearchCards";

// Components
import Textfield from "../inputs/input";
import Dropdown from "../inputs/dropdown";
import Autocomplete from "../inputs/Autocomplete";
import PaymentMethodButton from "./paymentMethodButton";

import { paymentMethods } from "../../models/PaymentMethods";
import { currenciesEnum } from "../../models/EnumEntity";
import { utils } from "../../utils/utils";
import Searchbox from "../inputs/searchbox";
import useStyles from './styles';


const PaymentDropdown = ({
  orderId,
  payment,
  currency,
  onChange,
  onChangeAmount,
  showAmount,
  business,
  checks,
  banks,
  currencyPrices,
  paymentMethodsHook
}) => {
  const [card, setCard] = useState();
  const [cardsByBank, setCardsByBank] = useState([]);
  const [inputAmount, setInputAmount] = useState(0);
  const [price, setPrice] = useState(1);
  const { classes } = useStyles();

  const [error, setError] = useState(false);
  const {
    loading,
    searchCard,
    searchCardPlans,
    searchCardWithAnAmount,
    searchCardPlansWithAnAmount,
  } = useCard(orderId);

  const { searchCardsByBank } = useSearchCards();
  const { Notify, variants } = useNorify();

  useEffect(() => {
    setInputAmount(parseFloat((payment.amount / price).toFixed(2)))
  }, [payment.amount, price])

  useEffect(() => {
    const currentPrice = currencyPrices?.find(c => c.origin?.code === payment.paymentMethodCurrency)?.price || 1;
    setPrice(currentPrice);
  }, [payment.paymentMethodCurrency, currencyPrices])


  useEffect(() => {
    if (!!card && card.installments.length > 0) {
      const installment = card.installments.find(i => i.id === payment?.installmentId) || card.installments[0];

      onChange({
        ...payment,
        bankId: card.bank.id,
        cardId: card.id,
        installmentId: installment.id,
        planId: installment.planId,
        installmentHasDiscount: installment.discountRate || 0,
      });
    }
    //TODO: Revisar esta parte
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card]);

  useEffect(() => {
    if (payment?.bankId) {
      searchCardsByBank(payment?.bankId)
        .then(cards => setCardsByBank(cards));
    }
  }, [payment?.bankId, searchCardsByBank]);

  useEffect(() => {
    const selectCard = async () => {
      const selectedCard = await searchCard(payment?.binNumber)
      setCard(selectedCard)
    }

    if (payment.cardId && payment.binNumber && !card) {
      selectCard();
    }
  }, [payment?.cardId, payment?.binNumber])

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      const bin = payment.binNumber;
      if (bin.length === 5 && business.country === 'UY')
        getPlans(bin);
    }
  }

  const getPlans = (bin) => {
    if (!showAmount) {
      searchCard(bin)
        .then(card => setCard(card))
        .catch(() => {
          Notify("No se encontro un plan para el bin ingresado. Ingrese los datos manualmente", variants.INFO);
          setError(true);
        });

      return;
    }

    if (!payment.amount || payment.amount <= 0)
      Notify("Ingrese un monto para obtener la tarjeta", variants.INFO);

    searchCardWithAnAmount(bin, parseFloat(payment.amount))
      .then(card => setCard(card))
      .catch(() => {
        Notify("No se encontro un plan para el bin ingresado. Ingrese los datos manualmente", variants.INFO);
        setError(true);
      });
  }

  const handlerBinChange = (event) => {
    setError(false)
    const bin = event.target.value;

    if (bin.length <= 6) {
      onChange({ ...payment, bankId: null, cardId: null, binNumber: bin });
      setCard(null);
    }

    if (bin.length === 6)
      getPlans(bin);
  };

  const handleBankChange = (e, option) => {
    onChange({ ...payment, cardId: null, bankId: option?.id });
    setCard(null);
  };

  const handleInputChange = (event) =>
    onChange({
      ...payment,
      [event.target.name]: event.target.value
    })

  const handleCheckAmountChange = (event) =>
    onChange({
      ...payment,
      checkAmount: parseFloat(event.target.value)
    })

  const handleCardBrandChange = (e, option) => {
    const cardId = option?.id;
    onChange({ ...payment, cardId });

    if (!cardId) {
      setCard(null);
      return;
    }

    if (!showAmount) {
      searchCardPlans(payment?.bankId, cardId, payment?.binNumber)
        .then(card => setCard(card))
        .catch(() => Notify("No se encontro un plan para el banco y tarjeta ingresados.", variants.ERROR));
      return;
    }

    if (payment.amount && payment.amount <= 0) {
      Notify("Ingrese un monto para obtener la tarjeta");
      return;
    }

    searchCardPlansWithAnAmount(
      payment?.bankId,
      cardId,
      payment?.binNumber,
      parseFloat(payment.amount)
    )
      .then(card => setCard(card))
      .catch(() => Notify("No se encontro un plan para el banco y tarjeta ingresados.", variants.ERROR));
  };

  const handlerInstallmentChange = (event) => {
    const installment = card.installments
      .find((i) => i.id === event.target.value);

    onChange({
      ...payment,
      installmentId: installment.id,
      planId: installment.planId,
      installmentHasDiscount: installment.discountRate || 0,
    });
  };

  const showInstallments = () =>
    card && card.installments.length &&
    (payment?.binNumber?.toString().length === 6 || payment?.card)

  const handleChangeAmount = (event) => {
    const amount = Number(event.target.value);
    setInputAmount(amount);
    onChangeAmount(amount * price);
    setCard(null);
  }

  const handleLastDigitChange = (event) => {
    const lastDigits = event.target.value;
    if (!utils.ValidateValueIsString(lastDigits) && lastDigits.length <= 4) {
      onChange({ ...payment, lastCardDigits: lastDigits })
    }
  }

  return (
    <Grid container spacing={1} justifyContent='center'>
      <Grid size={12}>
        <Grid container justifyContent='center' style={{ gap: '8px' }}>
          {
            paymentMethodsHook
              .map(p => (
                <Grid key={p.value}>
                  <PaymentMethodButton
                    paymentMethod={p}
                    isSelected={payment?.paymentMethodType === p.value}
                    onClick={(value) => onChange({ ...payment, paymentMethodType: value })}
                  />
                </Grid>
              ))
          }
        </Grid>
      </Grid>
      {
        payment?.paymentMethodType === paymentMethods.Cash &&
        <Grid size={12}>
          <Dropdown
            fullWidth
            size="small"
            name="paymentMethodCurrency"
            label="Seleccione una moneda"
            placeholder="Moneda"
            helperText={payment?.paymentMethodCurrency !== business?.defaultCurrency ? `Cotización: 1 ${payment.paymentMethodCurrency} = ${utils.formatMoney(price)} ${business.defaultCurrency}` : ""}
            onChange={handleInputChange}
            values={business.currencies || []}
            value={payment?.paymentMethodCurrency}
            getOptionLabel={(option) => option}
            getOptionValue={(option) => option}
            disabled={business.currencies.length <= 1}
          />
        </Grid>
      }
      {showAmount && (
        <Grid size={12}>
          <Searchbox
            fullWidth
            size="small"
            name="amount"
            label="Ingresá el monto a pagar"
            onChange={handleChangeAmount}
            value={inputAmount || ""}
            helperText={!!payment.amount && payment?.paymentMethodCurrency !== business?.defaultCurrency ? `${utils.formatMoney(payment.amount)} ${business.defaultCurrency}` : ""}
          />
        </Grid>
      )}
      {(payment?.paymentMethodType === paymentMethods.Check) && (
        <Grid size={12}>
          <Searchbox
            fullWidth
            size="small"
            name="checkAmount"
            label="Ingresá el monto del cheque"
            onChange={handleCheckAmountChange}
            value={(payment?.checkAmount && (payment?.checkAmount % 1 === 0
              ? payment?.checkAmount
              : payment?.checkAmount.toFixed(2))) || ""}
          />
        </Grid>
      )}
      {payment?.paymentMethodType === paymentMethods.Check && (
        <Grid size={12}>
          <Dropdown
            fullWidth
            size="small"
            name="checkId"
            label="Seleccione un tipo de cheque"
            placeholder="Cheque"
            onChange={handleInputChange}
            values={checks}
            value={payment?.checkId || ""}
            getOptionLabel={(option) => `${option?.description}`}
            getOptionValue={(option) => option?.id}
          />
        </Grid>
      )}
      {(payment?.paymentMethodType === paymentMethods.Card ||
        payment?.paymentMethodType === paymentMethods.Posnet) && (
          <>
            <Grid size={6}>
              <Textfield
                fullWidth
                size="small"
                name="Bin"
                label="Ingresa el bin de la tarjeta"
                onChange={handlerBinChange}
                onKeyPress={handleKeyPress}
                value={payment?.binNumber || ""}
                disabled={loading}
                helperText={business.country === 'UY' ? "Sí el BIN es de 5 digitos, presiona Enter para buscar" : ""}
              />
            </Grid>
            <Grid size={6}>
              <Textfield
                fullWidth
                name="lastCardDigits"
                size="small"
                type="number"
                label="Ultimos 4 digitos"
                onChange={handleLastDigitChange}
                value={payment?.lastCardDigits || ""}
                InputProps={{
                  inputProps: {
                    min: 0,
                  },
                }}
                disabled={loading}
              />
            </Grid>
            <Grid size={12}>
              <Autocomplete
                fullWidth
                size="small"
                name="cardBank"
                label="Seleccione un banco"
                placeholder="Banco"
                disabled={loading || !error}
                options={banks}
                loading={loading}
                value={banks?.find(b => b.id === payment?.bankId) ?? null}
                getOptionLabel={(option) => option?.name}
                onChange={handleBankChange}
              />
            </Grid>
            <Grid size={12}>
              <Autocomplete
                fullWidth
                size="small"
                name="cardBrand"
                label="Seleccione un tipo"
                placeholder="Tarjeta"
                disabled={!payment?.bankId || loading || !error}
                options={cardsByBank}
                loading={loading}
                value={cardsByBank?.find(b => b.id === payment?.cardId) ?? null}
                getOptionLabel={(option) => option?.name}
                onChange={handleCardBrandChange}
              />
            </Grid>
            {loading && (
              <Grid container alignItems="center" direction="column">
                <Grid>
                  <CircularProgress />
                </Grid>
                <Grid>
                  <Typography>Buscando medio de pago</Typography>
                </Grid>
              </Grid>
            )}
            {!loading && showInstallments() && (
              <Grid size={12}>
                <Dropdown
                  fullWidth
                  size="small"
                  onChange={handlerInstallmentChange}
                  placeholder="Medio de pago"
                  name="cardInstallments"
                  getOptionStyles={(option) => (option.governmentPlan || option.discountRate > 0) ? classes.governmentPlan : ""}
                  getOptionLabel={(option) =>
                    `${option?.displayText} ${option?.interestRate === 0
                      ? currency !== currenciesEnum.UYU ? '(S/ INTERES)' : ""
                      : `(C/ ${option?.interestRate}% DE INTERES ${currency} ${option?.amount})`
                    } (${currency} ${option?.amountPerInstallment} por cuota)`
                  }
                  getOptionValue={(option) => option?.id}
                  value={payment?.installmentId || ""}
                  values={card?.installments.filter(i => !showAmount || i.discountRate <= 0) || []}
                  SelectDisplayProps={{ "data-testid": "cardInstallment" }}
                />
              </Grid>
            )}
          </>
        )}
    </Grid>
  );
};

export default PaymentDropdown;
